import React, { use, useEffect, useMemo, useState } from "react";
import { UserInformationSection } from "./styled";
import Input from "components/Input/index";
import Select from "components/Select/index";
import ProfileImageUpload from "components/ProfileImageUpload";
import { useDropdownSlice } from "store/dropdownOptions/slice/index";
import { selectCompanies } from "store/dropdownOptions/slice/selector";
import { useDispatch, useSelector } from "react-redux";
import { useUsersSlice } from "pages/Users/slice/index";
import { selectProfileUploadInfo } from "pages/Users/slice/selectors";

export const EditProfileInfo = ({
  marketUserDetails,
  editInformation,
  setEditInformation
}) => {
  const { actions: dropdownActions } = useDropdownSlice();
  const { actions: usersActions } = useUsersSlice();
  const { data: companies } = useSelector(selectCompanies);
  const { loading: profileImageUploading, profile_image } = useSelector(
    selectProfileUploadInfo
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (profile_image) {
      setEditInformation({
        ...editInformation,
        profile_image: profile_image
      });
    }
  }, [profile_image]);

  /* ---------- Company options ---------- */
  const companyOptions = useMemo(() => {
    return (
      companies?.map((company) => ({
        value: company.id,
        label: company.company_name?.trim()
      })) || []
    );
  }, [companies]);

  const preFillCompany = useMemo(() => {
    if (marketUserDetails?.company_id && companyOptions.length > 0) {
      const matchedCompany = companyOptions.find(
        (option) => option.value === marketUserDetails?.company_id
      );
      return matchedCompany ? matchedCompany.value : "";
    }
    return "";
  }, [marketUserDetails, companyOptions]);

  // Fetch companies only if not already loaded
  useEffect(() => {
    dispatch(dropdownActions.getCompaniesRequest({}));
  }, []);

  // update user details when marketUserDetails changes
  useEffect(() => {
    if (marketUserDetails) {
      setEditInformation({
        phone_number: marketUserDetails?.phone_number || "",
        country_code: marketUserDetails?.country_code || "",
        company: preFillCompany,
        email: marketUserDetails?.email || "",

        first_name: marketUserDetails?.first_name || "",
        last_name: marketUserDetails?.last_name || "",
        designation: marketUserDetails?.designation || "",
        linkedin_url: marketUserDetails?.linkedin_url || "",
        profile_image: marketUserDetails?.profile_image || ""
      });
    }
  }, [marketUserDetails, preFillCompany]);

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, "");

    if (val.length <= 10) {
      setEditInformation((prev) => ({
        ...prev,
        phone_number: val
      }));
    }
  };

  const linkedinUrlValidation = (url) => {
    if (!url) return true;

    const pattern =
      /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[A-Za-z0-9_-]+\/?(\?.*)?$/i;
    return pattern.test(url.trim());
  };

  const [linkedinError, setLinkedinError] = useState("");

  const handleLinkedinBlur = (e) => {
    const url = e?.target?.value || "";
    const valid = linkedinUrlValidation(url);
    if (!valid) {
      setLinkedinError(
        "Please enter a valid LinkedIn profile URL (e.g. https://www.linkedin.com/in/username)"
      );
    } else {
      setLinkedinError("");
    }
  };

  // Handle profile image change
  const handleProfileImageChange = (file) => {
    dispatch(
      usersActions.uploadProfileImage({
        user_id: marketUserDetails?.id,
        file
      })
    );
  };

  return (
    <>
      <UserInformationSection>
        <h3 className="section-title">Primary Details</h3>
        <div className="edit-info-container">
          <Input
            id="phone"
            codeId="phone_id"
            type="tel"
            label="Login Phone Number *"
            countryCodePlaceholder="+91"
            placeholder="Enter Phone Number"
            value={editInformation?.phone_number}
            country_code_value={editInformation.country_code}
            onChange={handlePhoneChange}
            size="M"
            disabled
          />
          <div className="note-info-section">
            <span>NOTE:</span>
            <p>
              These details are used for account access and login. Changing the
              phone number or email may affect how the user signs in and
              receives account-related communication. Please review carefully
              before saving.
            </p>
          </div>

          <Input
            id="email"
            label="Email *"
            placeholder="john.doe@myhaulstore.com"
            value={editInformation?.email}
            onChange={(e) =>
              setEditInformation({
                ...editInformation,
                email: e.target.value
              })
            }
            size="M"
            disabled
          />
        </div>
      </UserInformationSection>

      <UserInformationSection>
        <h3 className="section-title">Primary Details</h3>
        <div className="edit-info-container">
          <Input
            id="first_name"
            label="First Name *"
            placeholder="John"
            value={editInformation?.first_name ?? ""}
            onChange={(e) =>
              setEditInformation({
                ...editInformation,
                first_name: e.target.value
              })
            }
            size="M"
          />
          <Select
            id="company"
            label="Company"
            value={editInformation?.company ?? ""}
            placeholder="Select Company"
            options={companyOptions}
            onChange={(selectedOption) =>
              setEditInformation({
                ...editInformation,
                company: selectedOption.value
              })
            }
            disabled
          />
          <Input
            id="last_name"
            label="Last Name *"
            placeholder="Doe"
            value={editInformation?.last_name ?? ""}
            onChange={(e) =>
              setEditInformation({
                ...editInformation,
                last_name: e.target.value
              })
            }
            size="M"
          />
          <Input
            id="designation"
            label="Designation"
            placeholder="Enter designation here"
            value={editInformation?.designation ?? ""}
            onChange={(e) =>
              setEditInformation({
                ...editInformation,
                designation: e.target.value
              })
            }
            size="M"
          />
          <Input
            id="linkedin_url"
            label="LinkedIn URL"
            placeholder="Enter LinkedIn URL"
            value={editInformation?.linkedin_url ?? ""}
            onChange={(e) =>
              setEditInformation({
                ...editInformation,
                linkedin_url: e.target.value
              })
            }
            inputProps={{
              onBlur: handleLinkedinBlur,
              onMouseOut: handleLinkedinBlur
            }}
            hasError={!!linkedinError}
            helperText={linkedinError}
            size="M"
          />

          <ProfileImageUpload
            label="Profile Image"
            onChange={handleProfileImageChange}
            maxSize={5 * 1024 * 1024}
            acceptedFormats={["image/png", "image/jpeg", "image/jpg"]}
            acceptedFormatsText="PNG, JPG, JPEG"
            maxSizeText="5MB"
            isPreview={true}
            uploadLoading={profileImageUploading}
            previewImage={editInformation?.profile_image}
            onRemove={() =>
              setEditInformation({
                ...editInformation,
                profile_image: ""
              })
            }
          />
        </div>
      </UserInformationSection>
    </>
  );
};
